import type {
  AdminUserDetail,
  AdminUserPause,
  AdminUserSubscription,
  AdminUserWebFunnelChannel,
} from "api/adminApi";

// 회원관리 화면의 표시 변환 모음. JSX 없이 순수 함수만 둔다 (테스트 대상).
// AdminPage.tsx 안에도 같은 이름의 로컬 헬퍼가 있지만 가져다 쓰지 않는다 —
// 회원관리는 체험 미리보기가 다루지 않던 상태(휴면·종결 권고 등)까지 표시해야 해서 매핑이 다르다.

const PROGRESS_STATUS_LABEL: Record<string, string> = {
  preparing: "준비 중",
  in_progress: "이용 중",
  terminated: "종료",
  recommended_stop: "종결 권고",
  leave: "탈퇴",
  auto_stop: "자동 종료",
};

export const progressStatusLabel = (status?: string | null) =>
  (status && PROGRESS_STATUS_LABEL[status]) || status || "알 수 없음";

const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
  freetrial: "체험 중",
  freetrial_expired: "체험 만료",
  subscribing: "이용 중",
  subscribing_expired: "이용 종료",
};

export const subscriptionStatusLabel = (status?: string | null) =>
  (status && SUBSCRIPTION_STATUS_LABEL[status]) || status || "없음";

const PLAN_LABEL: Record<string, string> = {
  starter: "스타터",
  challenger: "챌린저",
  master: "마스터",
};

export const planLabel = (plan?: string | null) =>
  (plan && PLAN_LABEL[plan]) || plan || "없음";

const OAUTH_ISSUER_LABEL: Record<string, string> = {
  kakao: "카카오",
  google: "구글",
  apple: "애플",
  naver: "네이버",
};

export const oauthIssuerLabel = (issuer?: string | null) =>
  (issuer && OAUTH_ISSUER_LABEL[issuer]) || issuer || "알 수 없음";

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  ready: "대기",
  success: "승인 대기",
  confirmed: "결제 완료",
  canceled: "취소",
  failed: "실패",
};

export const paymentStatusLabel = (status?: string | null) =>
  (status && PAYMENT_STATUS_LABEL[status]) || status || "알 수 없음";

const VENDOR_TYPE_LABEL: Record<string, string> = {
  toss_payments: "토스",
  app_store: "앱스토어",
  play_store: "플레이스토어",
  steppay: "스텝페이",
};

export const vendorTypeLabel = (vendor?: string | null) =>
  (vendor && VENDOR_TYPE_LABEL[vendor]) || vendor || "알 수 없음";

// 정지 상태 한 줄. 무기한이면 종료 예정일이 없다는 것을 분명히 말해 준다.
export const pauseStateLabel = (pause: AdminUserPause) => {
  if (!pause.isPaused) return "정지 아님";
  if (!pause.resumedAt) return "무기한 정지";
  return `${formatDate(pause.resumedAt)} 재개 예정`;
};

// "2026-08-27T14:03:22" 또는 "2026-08-27" 을 "2026. 8. 27." 로. 값이 없으면 빈 문자열.
export const formatDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatAmount = (amount?: number | null) =>
  amount == null ? "" : `${amount.toLocaleString("ko-KR")}원`;

// yyyy-MM-dd — date 인풋 값과 서버 파라미터가 같은 형식이다.
export const toDateInputValue = (date: Date) => {
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayInputValue = () => toDateInputValue(new Date());

/**
 * 재개 시 자동 복원될 만료일. 서버 계산과 같은 식이다:
 *   새 만료일 = 현재 만료일 + (정지 일수) + (추가 일수)
 * 화면이 이 값을 미리 보여줘야 운영자가 "직접 지정"이 필요한 경우를 알아본다
 * (옛 스크립트로 정지된 회원은 만료일이 이미 정지 시점으로 밀려 있어 자동 계산이 맞지 않는다).
 */
export const restoredExpirationDate = (
  subscription: AdminUserSubscription,
  pause: AdminUserPause,
  extraDays: number
): string => {
  const base = subscription.subscriptionExpirationDate;
  if (!base) return "";
  const date = new Date(base);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + (pause.pausedDays ?? 0) + extraDays);
  return toDateInputValue(date);
};

// 이용 상태 배지 톤 — 색은 장식이 아니라 상태를 읽는 축이다.
export const subscriptionTone = (
  pause: AdminUserPause,
  subscription: AdminUserSubscription
): "ok" | "warn" | "idle" | "info" => {
  if (pause.isPaused) return "warn";
  if (
    subscription.status === "subscribing" ||
    subscription.status === "freetrial"
  )
    return "ok";
  if (!subscription.hasSubscription) return "info";
  return "idle";
};

// 액션 바의 버튼 종류. 서버 엔드포인트와 1:1 이다.
export type AdminUserAction =
  | "extend"
  | "pause"
  | "resume"
  | "terminate"
  | "nickname"
  | "coach";

/**
 * 이 회원에게 이 처리를 할 수 없는 이유. 할 수 있으면 null.
 *
 * 판정 기준을 서버(AdminUserBO)가 실제로 던지는 전제조건에 맞춘다. 화면이 서버보다 넓게 막으면
 * 정상 처리를 눌러 보지도 못하고, 좁게 막으면 눌러야만 안 된다는 걸 알게 된다.
 * 만료일은 서버가 시각까지 보는데 화면은 날짜만 받으므로, "오늘 만료" 처럼 애매한 경우는 막지 않고
 * 서버가 판정하게 둔다.
 */
export const actionBlockReason = (
  detail: AdminUserDetail,
  action: AdminUserAction
): string | null => {
  const { user, subscription, pause, coach } = detail;

  if (user.progressStatus === "leave") return "탈퇴한 계정이에요";

  // 구독 행과 만료일이 있어야 기간을 다룰 수 있다. 준비 중(결제 전) 회원이 여기 걸린다.
  const hasSubscription =
    subscription.hasSubscription && !!subscription.subscriptionExpirationDate;
  // 서버 pause 의 전제조건과 같은 식이다.
  const isSubscribing =
    subscription.status === "subscribing" &&
    user.progressStatus === "in_progress";

  switch (action) {
    case "extend":
      return hasSubscription ? null : "구독 정보가 없는 회원이에요";

    case "pause":
      if (pause.isPaused) return "이미 일시정지 중이에요";
      if (!isSubscribing) return "이용 중인 회원만 정지할 수 있어요";
      if (isExpired(subscription.subscriptionExpirationDate))
        return "이용 기한이 이미 지났어요";
      return null;

    case "resume":
      return pause.isPaused ? null : "일시정지 중인 회원이 아니에요";

    // 이미 끝난 회원을 또 종료하지 않는다. 일시정지는 내부적으로 progressStatus 가 종료로 저장될 뿐
    // 운영자가 보는 상태는 "정지 중" 이라, 무기한 정지 회원의 조기 종료는 열어 둔다.
    case "terminate":
      if (!hasSubscription) return "구독 정보가 없는 회원이에요";
      if (!isSubscribing && !pause.isPaused)
        return "이용 중이거나 정지 중인 회원만 종료할 수 있어요";
      return null;

    case "coach":
      return coach ? null : "배정된 코치가 없어요";

    // 닉네임은 이용 상태와 무관하게 언제든 바꿀 수 있다.
    case "nickname":
    default:
      return null;
  }
};

// 만료일이 오늘보다 확실히 앞인지. 오늘이면 시각을 알 수 없으므로 지났다고 보지 않는다.
const isExpired = (expirationDate?: string | null) => {
  if (!expirationDate) return true;
  const expiration = new Date(expirationDate);
  if (Number.isNaN(expiration.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiration.setHours(0, 0, 0, 0);
  return expiration.getTime() < today.getTime();
};

// 웹퍼널 utm 파라미터를 한 줄로 합친다. 어떤 조합이 비어 있어도 오해가 없도록 키를 붙여서 낸다
// (값만 나열하면 medium 하나만 있을 때 source 로 읽힌다). 값이 하나도 없으면 null — 줄 자체를 뺀다.
export const utmSummary = (
  webFunnel: AdminUserWebFunnelChannel
): string | null => {
  const pairs: [string, string | null | undefined][] = [
    ["source", webFunnel.utmSource],
    ["medium", webFunnel.utmMedium],
    ["campaign", webFunnel.utmCampaign],
    ["content", webFunnel.utmContent],
    ["term", webFunnel.utmTerm],
  ];
  const parts = pairs
    .filter(([, value]) => !!value?.trim())
    .map(([key, value]) => `${key}=${value!.trim()}`);
  return parts.length ? parts.join(" · ") : null;
};
